/**************************************************
 * Componentes externos, de Material y de Angular *
 **************************************************/

import { MAT_STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { CurrencyPipe, registerLocaleData } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import es from "@angular/common/locales/es";
import { ApplicationRef, CUSTOM_ELEMENTS_SCHEMA, NgModule} from "@angular/core"; 
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule,DateAdapter, MatFormFieldModule, MatInputModule } from '@angular/material';
import { ServiceWorkerModule } from '@angular/service-worker';
import { MomentDateAdapter} from '@angular/material-moment-adapter';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ExtraOptions, Routes, RouterModule} from '@angular/router';
import { LandingView } from './components/landing-view/landing-view.component';
import { InterceptorService } from '@mova/components/core';
import { MvDialog } from '@mova/components/dialog';
import { MvDialogError } from '@mova/components/dialog-error';
import { MvLoginView } from "@mova/components/login-view";
import { MvLogoutBrokerViewModule } from "@mova/components/logout-broker-view";
import { MvLogoutBrokerView } from '@mova/components/logout-broker-view';
import { MvRateView } from "@mova/components/rate-view"; 
import { MvSnackBar } from '@mova/components/snack-bar';
import scss from 'highlight.js/lib/languages/scss';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import { HighlightModule } from 'ngx-highlightjs';
import { MatomoModule } from 'ngx-matomo';
import { AppComponent } from '../mova/components/app/app.component'; 
import { MaterialModule } from '../mova/modules/material.module'; 
import { MovaModule } from '../mova/modules/mova.module'; 

import { MvBrokerView } from '@mova/components/broker-view';
import { AuthGuard } from '@mova/components/core'; // AuthGuard
import { MvDeviceView } from '@mova/components/device-view';
import { MvInitView } from '@mova/components/init-view'; // Vista de inicio

import localeEs from '@angular/common/locales/es';
import { DataProtectionView } from "./components/data-protection-view/data-protection-view.component";

registerLocaleData(localeEs, 'es');

/*************************
 * Componentes de la App *
 *************************/

import { AccesoUsuarioRegistradoComponent } from './pages/acceso-usuario-registrado/acceso-usuario-registrado.component';
import { SidebarWizardComponent } from './shared/layout/sidebar-wizard/sidebar-wizard.component';
import { CargaFicheroCsvComponent } from './pages/carga-fichero-csv/carga-fichero-csv.component';
import { ValidacionQrComponent } from './pages/validacion-qr/validacion-qr.component';
import { CudiLoginViewComponent } from './components/cudi-login-view/cudi-login-view.component';
import { LoginViewComponent } from './pages/login-view/login-view.component';
import { NavbarComponent } from './shared/layout/navbar/navbar.component';
import { VerificarUsuarioComponent } from './pages/verificar-usuario/verificar-usuario.component';
import { ConfirmarAccesoComponent } from './pages/confirmar-acceso/confirmar-acceso.component';
import { SpinnerInterceptor } from './shared/interceptor/spinner/spinner.interceptor';
import { DescargaCarnetComponent } from './pages/descarga-carnet/descarga-carnet.component';

const MOVA_COMPONENTS = [AppComponent]; // Declarations
const MOVA_ENTRY_COMPONENTS = [
      MvDialog, 
      MvDialogError, 
      MvSnackBar,
      MvLoginView
]; // EntryComponents
const MOVA_PROVIDERS = {
  provide: HTTP_INTERCEPTORS,
  useClass: InterceptorService,
  multi: true
}; // Providers
const GENERAL_PROVIDERS = [
  ApplicationRef, 
  CurrencyPipe
];

/**********************************
 * Configuración de la navegación *
 **********************************/
export const MOVA_ROUTES: any = [
  { path: 'broker-view', component: MvBrokerView },
  { path: 'device-view', component: MvDeviceView },
  { path: 'init-view', component: MvInitView, canDeactivate: [AuthGuard] },
  { path: 'landing-view', component: LandingView },
  { path: 'login-view2', component: MvLoginView },
  { path: 'rate-view', component: MvRateView },
  {path: 'logout-broker', component: MvLogoutBrokerView},
 ];

 export const appRoutes: Routes = [
  ...MOVA_ROUTES, // MOVA -> Necesario para el funcionamiento de MOVA
  { path: 'landing-view', component: LandingView , canActivate: [AuthGuard] },
  { path: 'data-protection-view', component: DataProtectionView },
  { path: 'datos-usuario', component: AccesoUsuarioRegistradoComponent },
  { path: 'validar-carnet/:cdNifNie/:cdMailUniversidad', component: ValidacionQrComponent },
  { path: 'cargar-fichero', component: CargaFicheroCsvComponent},
  { path: 'verificar-usuario/:cdNifNie/:cdMailUniversidad', component: VerificarUsuarioComponent},
  { path: 'confirmar-acceso/:idUsuario', component: ConfirmarAccesoComponent},
  { path: 'descarga-carnet/:cdNifNie/:cdMailUniversidad', component: DescargaCarnetComponent},
  { path: 'login-view', component: LoginViewComponent},    
  { path: '', pathMatch: 'full', redirectTo: 'landing-view' },
 ];

 // Opciones de navegación
export const ROUTER_OPTIONS: ExtraOptions = {
  useHash: true,
  onSameUrlNavigation: 'reload',
  scrollPositionRestoration: 'disabled',
  anchorScrolling: 'enabled',
  scrollOffset: [0, 64], // Corregir esto cuando exista header de la plantilla
 };
registerLocaleData(es);
export function hljsLanguages() {
  return [
    {name: 'typescript', func: typescript},
    {name: 'scss', func: scss},
    {name: 'xml', func: xml}
  ];
}

/***************************
 * Configuración de fechas *
 ***************************/

/**
 * Formatos que se utilizarán en la aplicación para mostrar fechas.
 * Se pueden consultar los formatos disponibles aquí https://momentjs.com/docs/#/displaying/format/
 * Variarán dependiendo del locale establecido. En Mova está configurado el adaptador español.
 */
const MOMENT_FORMATS = {
  parse: {
    dateInput: 'L',
  },
  display: {
    dateInput: 'L',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  }
};

/********************
 * Módulo principal *
 ********************/

@NgModule({
  // Incluir los componentes en orden alfabético respetando el formato camelCase
  declarations: [
    ...MOVA_COMPONENTS, // MOVA -> Necesario para el funcionamiento de MOVA
    DataProtectionView,
    LandingView,
    AccesoUsuarioRegistradoComponent,
    SidebarWizardComponent,
    CargaFicheroCsvComponent,
    ValidacionQrComponent,
    CudiLoginViewComponent,
    LoginViewComponent,
    NavbarComponent,
    VerificarUsuarioComponent,
    ConfirmarAccesoComponent,
    DescargaCarnetComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MaterialModule,
    MatExpansionModule,
    MovaModule,
    MatFormFieldModule,
    MatInputModule,
    RouterModule.forRoot(appRoutes,ROUTER_OPTIONS),
    HighlightModule.forRoot({
      languages: hljsLanguages
    }),
    MatomoModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: false }),
  ],
  // Incluir los componentes que necesiten ser incluidos dinámicamente (mv-component-host)
  entryComponents: [
    ...MOVA_ENTRY_COMPONENTS, // MOVA -> Necesario para el funcionamiento de MOVA
    // HeaderAction1, // Componente de acción 1 del header
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    MOVA_PROVIDERS,
    GENERAL_PROVIDERS,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter
    },
    {
      provide: MAT_DATE_FORMATS, // Formatos para datepicker
      useValue: MOMENT_FORMATS
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'es-ES'
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true
    },
    ApplicationRef,
    CurrencyPipe,
    {
      provide: MAT_STEPPER_GLOBAL_OPTIONS,
      useValue:
      {
        displayDefaultIndicatorType: false,
        showError: true
      }
    }
  ],
  exports: [MvLogoutBrokerViewModule],
  bootstrap: [AppComponent]
})
export class AppModule { }
